<script setup lang="ts">
import { NumericDataSetDto } from '@/api/generator';
import ChartLegendSeriesIdentifier from '@/components/charts/ChartLegendSeriesIdentifier.vue';
import { getLegendItems } from '@/components/charts/utils/getLegendItems.ts';
import Dialog from '@/components/Dialog/Dialog.vue';
import { toStatisticsDisplayValue, getSetupParameterName } from '@/views/tasks/utils';
import IdentifierLabel from '../IdentifierLabel/IdentifierLabel.vue';
import { computed } from 'vue';

interface StatisticsDisplayProps {
  dataSets?: NumericDataSetDto[];
  modelValue: boolean;
}

const props = defineProps<StatisticsDisplayProps>();

const emits = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

function close() {
  emits('update:modelValue', false);
}

const legendItems = computed(() => getLegendItems(props.dataSets));
</script>

<template>
  <Dialog :modelValue="modelValue" size="x-large" @update:modelValue="close">
    <template #title>{{ $t('Statistics') }}</template>
    <template v-if="dataSets" #text>
      <v-table class="table-no-borders">
        <thead>
          <tr>
            <th scope="col">
              {{ $t('DataSet') }}
            </th>
            <th scope="col">
              {{ $t('Mean') }}
            </th>
            <th scope="col">
              {{ $t('StandardDeviation') }}
            </th>
            <th scope="col">
              {{ $t('Minimum') }}
            </th>
            <th scope="col">
              {{ $t('Maximum') }}
            </th>
            <th scope="col">
              {{ $t('Median') }}
            </th>
            <th scope="col">
              {{ $t('LowerQuartile') }}
            </th>
            <th scope="col">
              {{ $t('UpperQuartile') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(dataSet, index) in dataSets" :key="dataSet.id">
            <td>
              <div class="d-flex ga-3">
                <ChartLegendSeriesIdentifier
                  class="d-flex justify-center"
                  :symbol="legendItems?.[index].symbol"
                  :color="legendItems?.[index].color"
                  :enumeration="legendItems?.[index].seriesType === 'enumeration'"
                />
                <IdentifierLabel
                  class="evaluationParameter"
                  :identifier="dataSet.evaluationParameter.iuid"
                  :label="getSetupParameterName(dataSet.evaluationParameter)"
                />
              </div>
            </td>
            <td>
              {{
                toStatisticsDisplayValue(dataSet.evaluationParameter, dataSet.statistics?.mean, 1)
              }}
            </td>
            <td>
              {{
                toStatisticsDisplayValue(
                  dataSet.evaluationParameter,
                  dataSet.statistics?.standardDeviation,
                  1,
                )
              }}
            </td>

            <td>
              {{
                toStatisticsDisplayValue(dataSet.evaluationParameter, dataSet.statistics?.minimum)
              }}
            </td>
            <td>
              {{
                toStatisticsDisplayValue(dataSet.evaluationParameter, dataSet.statistics?.maximum)
              }}
            </td>
            <td>
              {{
                toStatisticsDisplayValue(dataSet.evaluationParameter, dataSet.statistics?.median, 1)
              }}
            </td>
            <td>
              {{
                toStatisticsDisplayValue(
                  dataSet.evaluationParameter,
                  dataSet.statistics?.quantile25,
                  1,
                )
              }}
            </td>
            <td>
              {{
                toStatisticsDisplayValue(
                  dataSet.evaluationParameter,
                  dataSet.statistics?.quantile75,
                  1,
                )
              }}
            </td>
          </tr>
        </tbody>
      </v-table>
    </template>
  </Dialog>
</template>
